import { useState } from 'react';
import { NavLink, Outlet, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';
import Button from '../../components/ui/Button';

const Estructura = styled.div`
  display: flex;
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.bg};
`;

const Lateral = styled.aside`
  width: 230px;
  background: ${({ theme }) => theme.colors.sageDeep};
  color: #f2f0ea;
  padding: ${({ theme }) => theme.space.lg} ${({ theme }) => theme.space.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
  flex-shrink: 0;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    position: fixed;
    inset: 0 auto 0 0;
    z-index: 40;
    transform: translateX(${({ $abierto }) => ($abierto ? '0' : '-100%')});
    transition: transform 250ms ease;
  }
`;

const LogoAdmin = styled(Link)`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: 1.4rem;

  span {
    font-size: 0.75rem;
    font-family: ${({ theme }) => theme.fonts.body};
    display: block;
    opacity: 0.7;
    letter-spacing: 0.15em;
    text-transform: uppercase;
  }
`;

const Menu = styled.nav`
  display: flex;
  flex-direction: column;
  gap: 0.2rem;
  flex: 1;
`;

const Item = styled(NavLink)`
  padding: 0.6rem 0.8rem;
  border-radius: ${({ theme }) => theme.radii.sm};
  font-size: 0.92rem;
  opacity: 0.85;
  transition: background 200ms ease, opacity 200ms ease;

  &:hover {
    background: rgba(255, 255, 255, 0.08);
    opacity: 1;
  }

  &.active {
    background: rgba(255, 255, 255, 0.16);
    opacity: 1;
    font-weight: 600;
  }
`;

const Contenido = styled.main`
  flex: 1;
  padding: ${({ theme }) => theme.space.xl} ${({ theme }) => theme.space.lg};
  max-width: 100%;
  overflow-x: hidden;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: ${({ theme }) => theme.space.lg} ${({ theme }) => theme.space.md};
    padding-top: 4.5rem;
  }
`;

const BarraMovil = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: flex;
    align-items: center;
    justify-content: space-between;
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 35;
    background: ${({ theme }) => theme.colors.sageDeep};
    color: #f2f0ea;
    padding: 0.6rem ${({ theme }) => theme.space.md};
  }
`;

const BotonMenu = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: inherit;
  border-radius: ${({ theme }) => theme.radii.sm};
  padding: 0.35rem 0.8rem;
  font-size: 0.85rem;
  cursor: pointer;
`;

const Fondo = styled.div`
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    display: ${({ $abierto }) => ($abierto ? 'block' : 'none')};
    position: fixed;
    inset: 0;
    background: rgba(47, 44, 39, 0.4);
    z-index: 39;
  }
`;

const secciones = [
  { a: '/admin', texto: 'Dashboard', exacta: true },
  { a: '/admin/reservas', texto: 'Reservas' },
  { a: '/admin/clases', texto: 'Clases' },
  { a: '/admin/tipos-clase', texto: 'Tipos de clase' },
  { a: '/admin/instructores', texto: 'Instructores' },
  { a: '/admin/clientes', texto: 'Clientes' },
  { a: '/admin/paquetes', texto: 'Paquetes' },
  { a: '/admin/reportes', texto: 'Reportes' },
];

function AdminLayout() {
  const { usuario, logout } = useAuth();
  const navigate = useNavigate();
  const [abierto, setAbierto] = useState(false);

  const salir = () => {
    logout();
    navigate('/admin/login');
  };

  return (
    <Estructura>
      <BarraMovil>
        <strong>numa. admin</strong>
        <BotonMenu onClick={() => setAbierto((v) => !v)} aria-expanded={abierto}>
          {abierto ? 'Cerrar' : 'Menú'}
        </BotonMenu>
      </BarraMovil>
      <Fondo $abierto={abierto} onClick={() => setAbierto(false)} />
      <Lateral $abierto={abierto}>
        <LogoAdmin to="/admin">
          numa.
          <span>Panel del estudio</span>
        </LogoAdmin>
        <Menu>
          {secciones.map(({ a, texto, exacta }) => (
            <Item key={a} to={a} end={exacta} onClick={() => setAbierto(false)}>
              {texto}
            </Item>
          ))}
        </Menu>
        <div>
          <p style={{ fontSize: '0.85rem', opacity: 0.75, marginBottom: '0.5rem' }}>
            {usuario.nombre} · {usuario.rol}
          </p>
          <Button $variante="secundario" $chico $completo onClick={salir} style={{ borderColor: 'rgba(255,255,255,0.4)', color: '#f2f0ea' }}>
            Cerrar sesión
          </Button>
        </div>
      </Lateral>
      <Contenido>
        <Outlet />
      </Contenido>
    </Estructura>
  );
}

export default AdminLayout;
